// src/core/mvcs/view/mediator/MagicWordsMediator.ts
import { ModelSignals } from '../../../signal/ModelSignals';
import { MagicWordsModel } from '../../model/states/MagicWordsModel';
import { MagicWordsView } from '../components/MagicWordsView';
import { TaskMediator } from '../TaskMediator';

/**
 * Mediator for the Magic Words chat feature.
 * Manages the lifecycle of data fetching, loading state visualization, 
 * and the distribution of Model data to the View via functional providers.
 */
export class MagicWordsMediator extends TaskMediator<MagicWordsView> {
    /**
     * Initializes the event listener for the MAGIC_WORDS_LOADED signal.
     * When the signal is emitted, the onWordsLoaded method is called.
     * This method is responsible for populating the MagicWordsView with the
     * data from the MagicWordsModel and triggering a layout update.
     */
    protected override initListener(): void {
        super.initListener();
        this.signalBus.on(ModelSignals.MAGIC_WORDS_LOADED, this.onWordsLoaded, this);
    }

    /**
     * Called when the view is added to the stage.
     * Shows the loading indicator and triggers a layout update.
     * Emits the ModelSignals.FETCH_MAGIC_WORDS signal to trigger the loading of the MagicWords data.
     */
    protected override onViewAddedToRoot(): void {
        super.onViewAddedToRoot();
        this.viewComponent.showLoading();
        this.triggerLayout();

        this.signalBus.emit(ModelSignals.FETCH_MAGIC_WORDS);
    }

    /**
     * Called when the MagicWordsModel emits the MAGIC_WORDS_LOADED signal.
     * Hides the loading indicator, populates the MagicWordsView with the chat rows, and triggers a layout update.
     * Logs a debug message indicating the number of chat rows rendered.
     */
    private onWordsLoaded(): void {
        console.log("[MagicWordsView] MagicWords loaded.");
        const model = this.modelMap.get<MagicWordsModel>(MagicWordsModel.NAME);
        const words = model.words;
        console.debug(`[MagicWordsView] New chat Words count is ${words.length}.`);

        const textureProvider = (id: string) => model.getTexture(id);
        const positionProvider = (name: string) => model.getPosition(name);

        this.viewComponent.hideLoading();
        this.viewComponent.buildRows(words, { textureProvider, positionProvider });

        this.triggerLayout();

        this.viewComponent.playChatEntrance();
    }

    /**
     * Removes all event listeners and cleans up the mediator for garbage collection.
     * Should be called when the mediator is no longer needed.
     * Offs the ModelSignals.MAGIC_WORDS_LOADED event listener.
     */
    public override onRemove(): void {
        this.signalBus.off(ModelSignals.MAGIC_WORDS_LOADED, this.onWordsLoaded);
        super.onRemove();
    }

    /**
     * Returns the MagicWordsView component.
     * Can be cast to the specific MagicWordsView type.
     */
    protected override get viewComponent(): MagicWordsView {
        return this.view;
    }
}